import { useState } from 'react';

import { EditorPlain } from '../../../../patterns/editor/components/editor-plain';
import { LabelWithoutRef } from '../../../../patterns/label/label-without-ref';
import { TextareaField } from '../../../../patterns/textarea/textarea-field';
import { Button } from '../../../../primitives/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../../primitives/sheet';
import { LoaderCircleIcon } from '../../../../svg/loader-circle-icon';
import { ToyBrickAddIcon } from '../../../../svg/toy-brick-add-icon';

type BlockFormSaveProps = {
  existingNames: string[];
  onSave: (name: string, description: string) => void | Promise<void>;
};

const normalizeName = (value: string) => value.replace(/\s/g, '').toLowerCase();

export function BlockFormSave({ existingNames, onSave }: BlockFormSaveProps) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasDuplicatedName, setHasDuplicatedName] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  return (
    <>
      <Button
        aria-label="Guardar bloque"
        className="size-7"
        data-block-add-button
        size="icon-sm"
        title="Guardar bloque"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <ToyBrickAddIcon />
      </Button>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          setHasDuplicatedName(false);
        }}
      >
        <SheetContent
          side="right"
          className="flex size-full flex-col"
          data-block-sheet-content
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onClick={(event) => event.stopPropagation()}
        >
          <SheetHeader>
            <SheetTitle>Guardar bloque</SheetTitle>
            <SheetDescription>
              Guarda este diseño como un bloque reutilizable para insertarlo rápidamente en
              cualquier plantilla.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4">
            <div className="grid gap-1">
              <LabelWithoutRef>Nombre</LabelWithoutRef>
              <EditorPlain
                counter={{ enable: true, hasWords: true }}
                disabled={isSaving}
                type="input"
                value={name}
                onChange={(value) => {
                  setName(value);
                  setHasDuplicatedName(false);
                }}
              />
              {hasDuplicatedName ? (
                <p className="text-sm text-destructive">Este nombre de bloque ya existe.</p>
              ) : null}
            </div>
            <div className="grid gap-1">
              <LabelWithoutRef>
                Descripción
                <span className="ml-1 text-[0.75rem] text-muted-foreground">opcional</span>
              </LabelWithoutRef>
              <TextareaField
                counter={{ enable: true, hasWords: true }}
                disabled={isSaving}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>
          <SheetFooter>
            <Button
              className="w-full"
              disabled={isSaving}
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              className="w-full"
              disabled={isSaving || !trimmedName}
              onClick={async () => {
                const isDuplicatedName = existingNames.some(
                  (existingName) => normalizeName(existingName) === normalizeName(trimmedName),
                );
                setHasDuplicatedName(isDuplicatedName);
                if (isDuplicatedName) return;

                setName(trimmedName);
                setDescription(trimmedDescription);
                setIsSaving(true);
                try {
                  await onSave(trimmedName, trimmedDescription);
                  setName('');
                  setDescription('');
                  setOpen(false);
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              {isSaving ? (
                <>
                  <LoaderCircleIcon className="mr-1 animate-spin" />
                  Procesando
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
